/**
 * Génère le `srcdoc` HTML complet pour rendre un email dans un iframe sandboxé.
 *
 * Pourquoi un fichier séparé : le parser Svelte interprète `<script>`, `</html>`,
 * etc. dans les string literals à l'intérieur des blocs `<script lang="ts">`.
 * En .ts pur, pas de parsing HTML → on peut écrire le doc tranquillement.
 *
 * Sécurité du doc :
 *   - viewport mobile correct → @media queries du mail s'appliquent
 *   - <base target="_blank"> → tous les liens ouvrent en nouvelle fenêtre
 *   - styles inline minimaux pour readability mobile
 *   - script de height-reporting via postMessage (l'iframe est sandboxed →
 *     parent peut pas lire contentDocument sans allow-same-origin)
 */

const INLINE_STYLES = `
  :root { color-scheme: light dark; }
  * { box-sizing: border-box; }
  html { overflow-x: hidden; } /* safety : zoom-to-fit script évite le débordement */
  html, body {
    margin: 0; padding: 12px;
    font: 14px/1.55 -apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif;
    color: #0e1013; background: #ffffff;
    word-wrap: break-word; overflow-wrap: anywhere;
    -webkit-text-size-adjust: 100%;
  }
  img { max-width: 100%; height: auto; border-radius: 4px; }
  a { color: #0b62d6; text-decoration: underline; text-underline-offset: 2px; }
  blockquote { border-left: 2px solid #d6d9df; margin: 6px 0; padding: 0 0 0 10px; color: #565a62; }
  pre, code { font-family: ui-monospace, "SF Mono", Menlo, monospace; background: rgba(0,0,0,0.05); border-radius: 4px; }
  code { padding: 1px 4px; font-size: 0.92em; }
  pre { padding: 8px 10px; overflow-x: auto; line-height: 1.5; font-size: 12px; }
  table { max-width: 100%; border-collapse: collapse; }
  td, th { padding: 4px 8px; }
  hr { border: none; border-top: 1px solid #e7e9ee; margin: 12px 0; }
  p { margin: 0 0 6px; } p:last-child { margin-bottom: 0; }
  ul, ol { margin: 4px 0; padding-left: 22px; }
  details.mf-quote { margin: 8px 0 0; padding: 0; border: none; }
  details.mf-quote > summary {
    cursor: pointer; list-style: none; display: inline-block;
    background: rgba(0,0,0,0.07); color: #565a62;
    border-radius: 10px; padding: 1px 8px; font-size: 11px; user-select: none;
  }
  details.mf-quote > summary::-webkit-details-marker { display: none; }
  details.mf-quote > summary::before { content: "⋯"; font-weight: 600; margin-right: 3px; }
  details.mf-quote[open] > summary { background: rgba(0,0,0,0.12); }
  @media (prefers-color-scheme: dark) {
    html, body { color: #f4f5f7; background: #0e1013; }
    a { color: #8fbcff; }
    blockquote { border-color: #2b3037; color: #a7abb3; }
    pre, code { background: rgba(255,255,255,0.07); }
    hr { border-color: #2b3037; }
    details.mf-quote > summary { background: rgba(255,255,255,0.08); color: #a7abb3; }
    details.mf-quote[open] > summary { background: rgba(255,255,255,0.12); }
  }
`;

/**
 * Génère le script qui :
 *   1. Mesure si le contenu déborde horizontalement (= mail non-responsive
 *      avec width fixed). Si oui, applique `zoom` CSS au body pour fit dans
 *      la viewport iframe (largeur écran). Plancher 0.4.
 *   2. Poste la hauteur finale (post-zoom) au parent via postMessage,
 *      pour que l'iframe ait la bonne hauteur sans scroll interne.
 */
function emailRendererScript(messageId: string): string {
  const id = JSON.stringify(messageId);
  return `
    (function() {
      var msgId = ${id};

      function fitWidth() {
        // Reset zoom à 1 pour mesurer la largeur naturelle
        document.body.style.zoom = '1';
        // Force reflow (lecture d'une prop layout)
        void document.body.offsetWidth;
        var viewportW = window.innerWidth;
        var contentW = Math.max(
          document.documentElement.scrollWidth,
          document.body.scrollWidth
        );
        if (contentW > viewportW + 4) {
          var ratio = viewportW / contentW;
          // Plancher 0.4 — en dessous illisible, l'user pinch-zoom au besoin
          document.body.style.zoom = String(Math.max(0.4, ratio));
        }
      }

      function postHeight() {
        var h = Math.max(
          document.documentElement.scrollHeight,
          document.body ? document.body.scrollHeight : 0,
          60
        );
        parent.postMessage({ type: 'mfx-h', id: msgId, h: h }, '*');
      }

      function update() {
        fitWidth();
        postHeight();
      }

      if (document.readyState === 'complete') update();
      else window.addEventListener('load', update);

      // Re-fit quand des images chargent (peuvent élargir le contenu)
      document.querySelectorAll('img').forEach(function(img) {
        if (!img.complete) {
          img.addEventListener('load', update, { once: true });
          img.addEventListener('error', update, { once: true });
        }
      });

      // Re-fit au resize (rotation iPhone, etc.)
      window.addEventListener('resize', update);

      // Re-fit au toggle des <details> (citations Gmail collapsed/expanded)
      document.addEventListener('toggle', postHeight, true);
    })();
  `;
}

/**
 * Construit le HTML complet à mettre dans `iframe.srcdoc`.
 * `sanitizedBody` doit déjà avoir été passé par sanitizeEmailHtml.
 */
export function buildEmailSrcdoc(sanitizedBody: string, messageId: string): string {
  // Construction par concat de tags (chaînes) — string literal multi-ligne
  // marche très bien ici en .ts (pas de parser Svelte).
  const styleOpen = "<sty" + "le>";
  const styleClose = "</sty" + "le>";
  const scriptOpen = "<scr" + "ipt>";
  const scriptClose = "</scr" + "ipt>";

  return (
    '<!DOCTYPE html>' +
    '<html lang="fr"><head>' +
    '<meta charset="utf-8">' +
    '<meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=yes">' +
    '<base target="_blank">' +
    styleOpen + INLINE_STYLES + styleClose +
    '</head><body>' + sanitizedBody + '</body>' +
    scriptOpen + emailRendererScript(messageId) + scriptClose +
    '</html>'
  );
}
